import { injectable, inject } from 'inversify';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import { SortOrder } from '@dealroadshow/uikit-table/DataTable/interfaces/SortOrder';
import Request from '@framework/api/Rpc/Request';
import { type IDispatcherFactory } from '@dataroom/json-rpc-dispatcher';
import DITypes from '@dataroom/json-rpc-dispatcher/DITypes';
import { ITransactionTypeGroupItem } from '../domain/vo/TransactionTypeGroupItem';
import { IDataroomListItem } from '../domain/vo/DataroomListItem';
import { IFetchCollectionResponse } from '@framework/state/useFetchCollection';

@injectable()
class RestrictedRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) private readonly rpc: IDispatcherFactory,
  ) {}

  getRestrictedTotalCount = async (
    payload: {
      search: string,
    },
    isPublicRequest: boolean,
  ): Promise<{ [key: string]: { count: number } }> => {
    const request = new Request(
      isPublicRequest ? 'dataroom.restricted.count.groups' : 'dataroom.restricted.count.groups.user_specific',
      payload,
    );
    const response = await this.rpc()
      .call<RpcSuccess>(request);

    return response.getResult().payload.groups;
  };

  getRestrictedList = async (payload: {
    sortBy: string,
    sortOrder: SortOrder,
    page?: number,
    perPage?: number | string,
    search: string,
    groupId: string,
    isPublicRequest: boolean,
  }): Promise<IFetchCollectionResponse<IDataroomListItem>> => {
    let {
      isPublicRequest,
      ...filters
    } = payload;

    const defaultFilters = {
      sortBy: 'name',
      sortOrder: 'asc',
    };

    if (filters.perPage === 'all') {
      const {
        perPage,
        page,
        ...otherFilters
      } = filters;
      filters = { ...otherFilters };
    }

    filters = { ...defaultFilters, ...filters };

    const request = new Request(
      isPublicRequest ? 'dataroom.restricted.list' : 'dataroom.restricted.list.user_specific',
      filters,
    );

    const response = await this.rpc()
      .call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getTransactionTypeGroups = async (): Promise<ITransactionTypeGroupItem[]> => {
    const request = new Request('dataroom.restricted.list.groups');
    const response = await this.rpc()
      .call<RpcSuccess>(request);

    return response.getResult().payload.groups;
  };

  requestAccess = async (payload: {
    dataroomId: number,
    role: string,
    message: string,
    salesRepresentative?: string,
  }): Promise<void> => {
    const request = new Request('dataroom.restricted.access_request.create', payload);
    const response = await this.rpc()
      .call<RpcSuccess>(request);

    return response.getResult().payload;
  };
}

export default RestrictedRepository;
